import type { Draft, DraftStore } from "@rhema/client";
import { toPlainText } from "@rhema/format";
import { useEffect, useReducer } from "react";
import { useClient } from "../session/session";

// Drafts kept on this device per chat and per thread, through the client's draft store.

/** The draft store, loaded from the device on first use, drawing again whenever a draft changes. */
export function useDrafts(): DraftStore {
  const drafts = useClient().drafts;
  const [, changed] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const off = drafts.subscribe(changed);
    if (!drafts.isLoaded) void drafts.load().catch(() => undefined);
    return off;
  }, [drafts]);
  return drafts;
}

/**
 * Writes waiting drafts to the device as the page hides or closes, so a draft typed just before
 * quitting is there next time.
 */
export function useFlushDrafts() {
  const drafts = useClient().drafts;
  useEffect(() => {
    const flush = () => void drafts.flush().catch(() => undefined);
    const hidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", hidden);
      flush();
    };
  }, [drafts]);
}

/** One line of a draft for the chat list, without its formatting marks. */
export function draftPreview(draft: Draft | undefined): string | undefined {
  if (!draft) return undefined;
  const text = toPlainText(draft.body, draft.format).replace(/\s+/g, " ").trim();
  // A draft that is only a reply still shows, so the person knows something is waiting there.
  return text || (draft.replyTo ? "Reply" : undefined);
}
